/**
 * Flow usage counter — "2 of 3 knods" in the dashboard header.
 *
 * Exists so the plan's cap is legible BEFORE someone collides with it, rather
 * than only as a refusal at the moment they try to create.
 *
 * Three cases carry the design:
 *
 * 1. **Unlimited** (`limit === -1`, the migration-097 sentinel). Rendering
 *    "12 of -1" would leak a database sentinel into the UI, and "12 of ∞" is
 *    noise. Unlimited plans see the bare count — still useful, and it keeps the
 *    element from appearing and disappearing as people change plan.
 *
 * 2. **Over limit** (`used > limit`). This is the case that matters. The
 *    trigger only fires on INSERT, so someone who subscribed, made twelve
 *    knods and then cancelled keeps all twelve. "12 of 3" is arithmetically
 *    honest and reads like a rendering bug, so the over-limit state changes
 *    shape instead of just changing colour: it states the count, then states
 *    what the plan includes, and the tooltip says the existing knods are safe.
 *    Nothing here implies data will be deleted, because none will be.
 *
 * 3. **Unknown / loading.** Renders an empty placeholder rather than a
 *    provisional "0 of 3" — on a cold dashboard the flow list has not arrived
 *    yet, and briefly claiming someone has no knods is worse than briefly
 *    showing nothing.
 *
 * Copy about what to do (delete, not archive) is centralised in
 * `src/utils/flowLimit.ts` so this and the refusal dialog cannot drift apart.
 */

import React from 'react';
import { UNLIMITED_FLOWS } from '../../utils/flowLimit';
import './FlowUsageCounter.css';

export type FlowUsageState = 'loading' | 'unlimited' | 'under' | 'at' | 'over';

export interface FlowUsageCounterProps {
  /** Flows the user currently has. null while unknown. */
  used: number | null;
  /** The plan's max_flows; -1 = unlimited. null while unknown. */
  limit: number | null;
  /** Force the placeholder even if numbers are present (e.g. a refetch). */
  loading?: boolean;
  className?: string;
}

const plural = (n: number) => (n === 1 ? 'knod' : 'knods');

/**
 * Pure classification, exported so the states can be asserted without
 * rendering. `used > limit` is checked before `used >= limit` on purpose —
 * over-limit is a distinct state, not a louder at-limit.
 */
export function resolveFlowUsageState(
  used: number | null | undefined,
  limit: number | null | undefined,
  loading = false,
): FlowUsageState {
  if (loading || typeof used !== 'number' || typeof limit !== 'number') return 'loading';
  if (limit === UNLIMITED_FLOWS || limit < 0) return 'unlimited';
  if (used > limit) return 'over';
  if (used >= limit) return 'at';
  return 'under';
}

/** The visible label and its tooltip for a resolved state. */
export function describeFlowUsage(
  state: FlowUsageState,
  used: number | null,
  limit: number | null,
): { label: string; title: string } {
  if (state === 'loading' || used === null || limit === null) {
    return { label: '', title: 'Counting your knods…' };
  }

  if (state === 'unlimited') {
    return {
      label: `${used} ${plural(used)}`,
      title: 'Your plan includes unlimited knods.',
    };
  }

  if (state === 'over') {
    return {
      label: `${used} ${plural(used)} · plan includes ${limit}`,
      title:
        `Your plan includes ${limit} ${plural(limit)} and you have ${used}. ` +
        `All ${used} are safe and stay exactly where they are — but you'll need to be back ` +
        `down to ${limit}, or on a bigger plan, before you can create another. ` +
        'Archiving keeps a knod in the count; only deleting frees a slot.',
    };
  }

  if (state === 'at') {
    return {
      label: `${used} of ${limit} ${plural(limit)}`,
      title:
        `You've used all ${limit} ${plural(limit)} your plan includes. ` +
        'Delete one, or upgrade, to create another. ' +
        'Archiving keeps a knod in the count; only deleting frees a slot.',
    };
  }

  return {
    label: `${used} of ${limit} ${plural(limit)}`,
    title: `Your plan includes ${limit} ${plural(limit)}. You have ${used}.`,
  };
}

export const FlowUsageCounter: React.FC<FlowUsageCounterProps> = ({
  used,
  limit,
  loading = false,
  className,
}) => {
  const state = resolveFlowUsageState(used, limit, loading);
  const { label, title } = describeFlowUsage(state, used, limit);

  return (
    <span
      className={`flow-usage-counter flow-usage-counter--${state}${className ? ` ${className}` : ''}`}
      data-testid="flow-usage-counter"
      data-state={state}
      // `status` (not `alert`) — it is ambient information, so a screen reader
      // should mention a change without interrupting what the user is doing.
      role="status"
      aria-busy={state === 'loading' || undefined}
      aria-label={state === 'loading' ? undefined : title}
      title={title}
    >
      {label}
    </span>
  );
};

export default FlowUsageCounter;
